import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Home, 
  FileText, 
  Calendar, 
  Settings, 
  QrCode,
  User,
  Shield,
  Heart,
  PawPrint,
  Plane,
  Building2,
  Wallet,
  Users,
  BookOpen,
  Accessibility,
  Bug
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

interface MobileNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
}

export function MobileLayout() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showDebug, setShowDebug] = useState(false);

  // Determine if we're on a module route
  const isModuleRoute = location.pathname !== '/mobile/dashboard' && 
                       location.pathname !== '/mobile/scanner' && 
                       location.pathname !== '/mobile/files' && 
                       location.pathname !== '/mobile/calendar' && 
                       location.pathname !== '/mobile/profile';

  // Core mobile navigation items
  const navItems: MobileNavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <Home size={20} />,
      path: '/mobile/dashboard'
    },
    {
      id: 'scanner',
      label: 'Scanner',
      icon: <QrCode size={20} />,
      path: '/mobile/scanner'
    },
    {
      id: 'files',
      label: 'Files',
      icon: <FileText size={20} />,
      path: '/mobile/files'
    },
    {
      id: 'calendar',
      label: 'Calendar',
      icon: <Calendar size={20} />,
      path: '/mobile/calendar'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: <User size={20} />,
      path: '/mobile/profile'
    }
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    const navItem = navItems.find(item => item.id === tabId);
    if (navItem) {
      navigate(navItem.path);
    }
  };

  // Quick action cards for dashboard
  const quickActions = [
    {
      id: 'digital',
      label: 'Digital Estate',
      icon: <Shield size={24} />,
      color: 'bg-purple-100 text-purple-600',
      path: '/mobile/digital'
    },
    {
      id: 'health',
      label: 'Health',
      icon: <Heart size={24} />,
      color: 'bg-red-100 text-red-600',
      path: '/mobile/health'
    },
    {
      id: 'pets',
      label: 'Pets',
      icon: <PawPrint size={24} />,
      color: 'bg-orange-100 text-orange-600',
      path: '/mobile/pets'
    },
    {
      id: 'travel',
      label: 'Travel',
      icon: <Plane size={24} />,
      color: 'bg-blue-100 text-blue-600',
      path: '/mobile/travel'
    },
    {
      id: 'legal',
      label: 'Legal',
      icon: <Building2 size={24} />,
      color: 'bg-gray-100 text-gray-600',
      path: '/mobile/legal'
    },
    {
      id: 'financial',
      label: 'Financial',
      icon: <Wallet size={24} />,
      color: 'bg-green-100 text-green-600',
      path: '/mobile/financial'
    },
    {
      id: 'family',
      label: 'Family',
      icon: <Users size={24} />,
      color: 'bg-pink-100 text-pink-600',
      path: '/mobile/family'
    },
    {
      id: 'ndis',
      label: 'NDIS',
      icon: <Accessibility size={24} />,
      color: 'bg-teal-100 text-teal-600',
      path: '/mobile/ndis'
    },
    {
      id: 'wellbeing',
      label: 'Wellbeing',
      icon: <Heart size={24} />,
      color: 'bg-pink-100 text-pink-600',
      path: '/mobile/wellbeing'
    },
    {
      id: 'legacy',
      label: 'Legacy',
      icon: <BookOpen size={24} />,
      color: 'bg-indigo-100 text-indigo-600',
      path: '/mobile/legacy'
    }
  ];

  const handleQuickAction = (path: string) => {
    navigate(path);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-idecide-primary px-4 py-3 shadow-sm pt-safe">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/idecide-logo.png" alt="iDecide" className="h-8 w-auto" />
            <div>
              <h1 className="text-lg font-semibold text-idecide-dark">iDecide</h1>
              <p className="text-xs text-idecide-dark opacity-80">
                Score: {profile?.idecide_score || 0}/500
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/mobile/profile')}
            className="text-idecide-dark"
          >
            <User size={20} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDebug(!showDebug)}
            className="text-idecide-dark"
          >
            <Bug size={20} />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'dashboard' && !isModuleRoute && (
          <div className="p-4 space-y-6">
            {/* Welcome Section */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Welcome back{profile?.full_name ? `, ${profile.full_name}` : ''}
              </h2>
              <p className="text-sm text-gray-600">
                Your iDecide Score: <span className="font-semibold">{profile?.idecide_score || 0}</span>/500
              </p>
            </div>

            {/* Quick Actions Grid */}
            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-3">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                {quickActions.map((action) => (
                  <Card
                    key={action.id}
                    className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleQuickAction(action.path)}
                  >
                    <div className="flex flex-col items-center text-center space-y-2">
                      <div className={`w-12 h-12 rounded-lg ${action.color} flex items-center justify-center`}>
                        {action.icon}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{action.label}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h3 className="text-md font-semibold text-gray-900 mb-3">Recent Activity</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-2 rounded bg-gray-50">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Updated health records</span>
                  <span className="text-xs text-gray-400 ml-auto">2h ago</span>
                </div>
                <div className="flex items-center gap-3 p-2 rounded bg-gray-50">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Added travel document</span>
                  <span className="text-xs text-gray-400 ml-auto">1d ago</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Module content or other tab content */}
        {(activeTab !== 'dashboard' || isModuleRoute) && (
          <Outlet />
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="bg-white border-t border-gray-200 px-2 py-2 pb-safe">
        <div className="flex justify-around">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleTabChange(item.id)}
              className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
                activeTab === item.id
                  ? 'text-idecide-primary bg-idecide-primary bg-opacity-10'
                  : 'text-gray-500 hover:text-idecide-primary'
              }`}
            >
              {item.icon}
              <span className="text-xs mt-1">{item.label}</span>
              {item.badge && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Debug Component - Removed DeviceDetectionDebug as it doesn't exist */}
    </div>
  );
} 